<template lang="pug">
    div.controls

        // Stop Streaming Link button
        v-tooltip(
            color="black"
            top
            v-if="type === 'live' && (isAdmin || isInstructor) && channel && channel.inputs"
        )
            template(v-slot:activator="{ on }")
                v-btn.button.mr-0.ml-0(
                    color="white"
                    v-on="on"
                    flat
                    :disabled="type !== 'live' || disableStopSession"
                    :loading="disableStopSession"
                    icon
                    @click="stop"
                )
                    v-icon.icon stop
            span(v-html="$t('sessions.list.stopStreamingHint')")

        // Copy Streaming Link button
        v-tooltip(
            color="black"
            top
            v-if="(type === 'planned' || type === 'live') && (isAdmin || isInstructor) && channel && channel.inputs"
        )
            template(v-slot:activator="{ on }")
                v-btn.button.mr-0(
                    color="white"
                    v-on="on"
                    flat
                    icon
                    @click="copyLink"
                )
                    v-icon.icon link
            span(v-html="$t('sessions.list.copyStreamingLinkHint')")

        // Force start planned Meditation button
        v-tooltip(
            v-if="showForceStart"
            color="black"
            top
        )
            template(v-slot:activator="{ on }")
                v-btn.button.mr-0(
                    class="button"
                    color="white"
                    v-on="on"
                    :loading="disableForcePlannedStart"
                    flat
                    icon
                    @click="handleForcePlannedStart(item.id)"
                )
                    v-icon.icon start
            span(v-html="$t('sessions.list.forceStart')")

        // Edit Meditation button
        v-tooltip(
            v-if="isAdmin"
            color="black"
            top
        )
            template(v-slot:activator="{ on }")
                v-btn.button.mr-0(
                    class="button"
                    color="white"
                    v-on="on"
                    flat
                    icon
                    @click="edit"
                )
                    v-icon.icon edit
            span(v-html="$t('sessions.list.editHint')" v-if="isInstructor")
            span(v-html="$t('general.edit')" v-if="isAdmin")
</template>

<script>
    import VueTypes from 'vue-types';
    import dayjs from 'dayjs';
    import Handler from '~services/Handler';
    import { mapActions, mapGetters } from 'vuex';

    export default {
        props: {
            item: VueTypes.object.def({}),
            type: VueTypes.string.def(''),
            sessionId: VueTypes.string.def(''),
            startedAt: VueTypes.date,
            channel: VueTypes.object.def({})
        },
        data() {
            return {
                disableStopSession: false,
                disableForcePlannedStart: false
            };
        },
        computed: {
            ...mapGetters('auth', ['isAdmin', 'isInstructor']),
            showForceStart() {
                const isValidUser = this.isAdmin || this.isInstructor;

                //We need to consider live because the cron service might set it to live anyways
                const isValidSession =
                    this.type === 'planned' || this.type === 'live';
                const hasNoChannels = !(
                    this.channel &&
                    this.channel.inputs &&
                    this.channel.inputs.length &&
                    this.channel.inputs[0] &&
                    this.channel.inputs[0].url
                );
                const currentDate = dayjs();
                const parsedIntervalStart = dayjs(this.item.startedAt).subtract(
                    4,
                    'minute'
                );
                const parsedIntervalEnd = dayjs(this.item.startedAt).add(
                    2,
                    'hour'
                );
                const isWithinCorrectTimeSlot =
                    currentDate.isAfter(parsedIntervalStart) &&
                    currentDate.isBefore(parsedIntervalEnd);

                // console.log(`Calculating forceStart => isValidUser:${isValidUser} && isValidSession: ${isValidSession} && hasNoChannels: ${hasNoChannels} && isWithinCorrectTimeSlot: ${isWithinCorrectTimeSlot}`, this.item, this.item.channel)
                return (
                    isValidUser &&
                    isValidSession &&
                    hasNoChannels &&
                    isWithinCorrectTimeSlot
                );
            }
        },
        methods: {
            ...mapActions('sessions', ['stopSession', 'forcePlannedStart']),
            /**
             * Copy link into buffer
             */
            copyLink: function() {
                let successMessage = this.$t('sessions.list.successMessage');
                let errorMessage = this.$t('sessions.list.errorMessage');
                let dummy = document.createElement('input');

                document.body.appendChild(dummy);

                if (
                    this.channel &&
                    this.channel.inputs &&
                    this.channel.inputs.length &&
                    this.channel.inputs[0] &&
                    this.channel.inputs[0].url
                ) {
                    dummy.value = this.channel.inputs[0].url;
                    dummy.select();
                    document.execCommand('copy');

                    Handler.handleResponse(
                        { ok: true },
                        successMessage,
                        errorMessage,
                        undefined
                    );
                }

                document.body.removeChild(dummy);
            },

            /**
             * Stop Meditation and Update Meditation type
             */
            async stop() {
                this.disableStopSession = true;

                // API request to Stop Meditation
                const resp = await this.stopSession(this.sessionId);

                // Update type
                if (resp) {
                    this.type = 'recorded';
                }

                this.disableStopSession = false;
            },

            /**
             * Edit Meditation
             */
            edit() {
                let currentDate = dayjs();
                let startedAt = dayjs(this.startedAt);
                let diffTime = startedAt.diff(currentDate, 'seconds');

                if (
                    this.isAdmin ||
                    (this.isInstructor && diffTime > 0 && diffTime <= 10 * 60)
                ) {
                    if (this.item.type === 'recorded-live') {
                        return this.$router.push({
                            name: 'dashboard.sessions.editRecordedLive',
                            params: { id: this.sessionId }
                        });
                    }
                    if (this.startedAt) {
                        return this.$router.push({
                            name: 'dashboard.sessions.edit',
                            params: { id: this.sessionId }
                        });
                    }

                    return this.$router.push({
                        name: 'dashboard.sessions.editRecorded',
                        params: { id: this.sessionId }
                    });
                }
            },

            /**
             * Forced planned session start
             */
            async handleForcePlannedStart() {
                this.disableForcePlannedStart = true;
                const response = await this.forcePlannedStart(this.item.id);
                if (!response.ok) {
                    const errorMessage = this.$t(
                        `sessions.codes.errors.${response.data.message}`
                    );
                    this.$store.dispatch('snackbar/showMessage', errorMessage);
                    this.disableForcePlannedStart = false;
                }
            }
        }
    };
</script>
